/**
 * Error suppression utilities for filtering out browser extension errors
 * that don't affect the application functionality
 */

/**
 * Suppress browser extension errors that clutter the console
 * These errors are common and don't affect the application's functionality
 */
export function suppressBrowserExtensionErrors(): () => void {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  
  const shouldSuppress = (message: string): boolean => {
    // Only suppress very specific browser extension errors, not application errors
    const lowerMessage = message.toLowerCase();
    
    // Check for runtime.lastError variations (most common)
    if (lowerMessage.includes('runtime.lasterror') || lowerMessage.includes('runtime.last_error')) {
      return true; // Suppress all runtime.lastError messages
    }
    
    // Check for connection errors
    if (lowerMessage.includes('could not establish connection') || 
        lowerMessage.includes('receiving end does not exist')) {
      return true;
    }
    
    // Check for other extension-related errors
    return (
      (message.includes('Unchecked runtime.lastError') && message.includes('message port closed')) ||
      (message.includes('Unchecked runtime.lastError') && message.includes('disconnected port object')) ||
      (message.includes('Unchecked runtime.lastError') && message.includes('Could not establish connection')) ||
      (message.includes('Unchecked runtime.lastError') && message.includes('back/forward cache')) ||
      (message.includes('Error handling response') && message.includes('disconnected port object')) ||
      (message.includes('chrome-extension://') && message.includes('ERR_FILE_NOT_FOUND')) ||
      (message.includes('chrome-extension://') && (message.includes('utils.js') || message.includes('extensionState.js') || message.includes('heuristicsRedefinitions.js'))) ||
      (message.includes('background.js') && message.includes('FrameDoesNotExistError')) ||
      (message.includes('background.js') && message.includes('DelayedMessageSender')) ||
      (message.includes('background.js') && message.includes('Frame 0 does not exist')) ||
      (message.includes('completion_list.html') && message.includes('chrome-extension://')) ||
      (message.includes('mfbcdcnpokpoajjciilocoachedjkima') && message.includes('ERR_FILE_NOT_FOUND')) ||
      (message.includes('FrameDoesNotExistError') && message.includes('Frame 0 does not exist in tab')) ||
      message.includes(' does not exist in tab ') ||
      (message.includes('DelayedMessageSender') && message.includes('Frame 0 does not exist')) ||
      message.includes("Cannot read properties of null (reading 'postMessage')") ||
      (message.includes('Error handling response') && message.includes('TypeError')) ||
      // Additional patterns for the specific errors you're seeing
      (message.includes('Uncaught (in promise) FrameDoesNotExistError')) ||
      (message.includes('Frame 0 does not exist in tab')) ||
      (message.includes('Attempting to use a disconnected port object')) ||
      (message.includes('Error handling response: Error: Attempting to use a disconnected port object')) ||
      (message.includes('chrome-extension://mfbcdcnpokpoajjciilocoachedjkima')) ||
      (message.includes('Could not establish connection. Receiving end does not exist')) ||
      (message.includes('The message port closed before a response was received')) ||
      // Emotion and React multiple instance patterns
      (message.includes('You are loading @emotion/react when it is already loaded')) ||
      (message.includes('Running multiple instances may cause problems')) ||
      (message.includes('This can happen if multiple versions are used')) ||
      (message.includes('or if multiple builds of the same version are used')) ||
      (message.includes('Invalid hook call')) ||
      (message.includes('Hooks can only be called inside of the body of a function component')) ||
      (message.includes('You might have mismatching versions of React and the renderer')) ||
      (message.includes('You might be breaking the Rules of Hooks')) ||
      (message.includes('You might have more than one copy of React in the same app')) ||
      (message.includes('Cannot read properties of null (reading \'useState\')')) ||
      (message.includes('@emotion/react')) ||
      (message.includes('multiple instances')) ||
      (message.includes('emotion-react.browser.development.esm.js')) ||
      (message.includes('emotion-styled-base.browser.development.esm.js')) ||
      (message.includes('emotion-styled.browser.development.esm.js'))
    );
  };

  console.error = (...args: any[]) => {
    const message = args.join(' ');
    if (shouldSuppress(message)) {
      // Suppress these errors as they're from browser extensions
      return;
    }
    // Log other errors normally
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    if (shouldSuppress(message)) {
      // Suppress these warnings as they're from browser extensions
      return;
    }
    // Log other warnings normally
    originalConsoleWarn.apply(console, args);
  };

  // Return a function to restore original console methods
  return () => {
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
  };
}

/**
 * Suppress browser extension errors for a specific duration
 * @param duration Duration in milliseconds to suppress errors (default: 15 seconds)
 * @returns Function to manually restore console methods
 */
export function suppressBrowserExtensionErrorsTemporarily(duration: number = 15000): () => void {
  const restore = suppressBrowserExtensionErrors();
  
  setTimeout(() => {
    restore();
  }, duration);
  
  return restore;
}

/**
 * Apply error suppression globally (alias for suppressBrowserExtensionErrors)
 * This is used by main.tsx for global error suppression
 */
export function applyErrorSuppression(): () => void {
  return suppressBrowserExtensionErrors();
}
